// Issue #141 — the Virginia COMET summary-first report preview. VA reports get the same polish the
// federal Form 3 report got (Form3SummaryView): a Statement-of-funds card + a horizontal cash-on-
// hand bridge, then each COMET schedule as an expandable section — instead of the older flat stack
// of tables. It renders ONLY when the report-detail payload carries `vaSchedules` (a Virginia
// report; federal keeps its own summary, AL keeps the flat view).
//
// Every summary NUMBER is DISPLAY ONLY: it comes straight off `vaSchedules`, which the backend
// derives from the SAME assembled rows the file builder consumes (via the shared schedule routing),
// so the preview cannot drift from the filed COMET file. This component never sums or recomputes a
// cross-foot line — it only sums the rows WITHIN a schedule for that schedule's own subtotal.
//
// The editable schedules (A/B contributions, D expenditures) reuse the report-detail's own editable
// items, so click-through-to-edit still works on a pre-filing (Ready) report; the read-only
// schedules (C receipts, E loans, F debts) are managed in the ledger, mirroring how the federal
// Schedule C loan ledger is read-only. Schedule E splits loans RECEIVED from loans REPAID rather
// than summing them (they are opposite cash flows — the prototype surfaced that a single E total is
// misleading), matching how the file discloses them.
import { useState, type ReactNode } from 'react';
import { ChevronRight, Pencil } from 'lucide-react';
import { PreflightCopy, assertNever, electionDesignationLabel, isOnReportInclusion } from '@signalvote/shared';
import type {
  VaCometScheduleViewDto,
  ContributionReportItem,
  ExpenditureReportItem,
  RefundReportItem,
  ReportRowInclusion,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { formatAmount, formatDate } from '../utils';

interface VaCometSummaryViewProps {
  vaSchedules: VaCometScheduleViewDto;
  contributionItems: ContributionReportItem[];
  expenditureItems: ExpenditureReportItem[];
  refundItems: RefundReportItem[];
  // Non-null only on a pre-filing (Ready) report; a Filed snapshot passes null → read-only rows.
  onEditContribution: ((contributionId: string) => void) | null;
  onEditExpenditure: ((expenditureId: string) => void) | null;
}

export function VaCometSummaryView({
  vaSchedules,
  contributionItems,
  expenditureItems,
  refundItems,
  onEditContribution,
  onEditExpenditure,
}: VaCometSummaryViewProps) {
  const gh = vaSchedules.scheduleGh;

  // "on the report" is Included plus any folded already-reported row, kept symmetric
  // with the off-filing filter below so a Reported row can never fall between the two sets.
  // (VA is WholeReport, so it never actually produces a Reported fold — this only keeps the
  // partition consistent with the AL/flat views.)
  const includedContributions = contributionItems.filter((i) => isOnReportInclusion(i.inclusion));
  const includedExpenditures = expenditureItems.filter((i) => isOnReportInclusion(i.inclusion));
  // Only genuinely off-filing rows go here; on-report rows (Included + any folded
  // already-reported) are excluded so a Reported row never lands in the off-filing section.
  const offFilingRows: OffFilingRow[] = [
    ...contributionItems
      .filter((i) => !isOnReportInclusion(i.inclusion))
      .map((i) => ({ id: i.id, date: i.date, name: i.contactName, amount: i.amount, inclusion: i.inclusion })),
    ...expenditureItems
      .filter((i) => !isOnReportInclusion(i.inclusion))
      .map((i) => ({ id: i.id, date: i.date, name: i.contactName, amount: i.amount, inclusion: i.inclusion })),
  ];

  const contributionsTotal = includedContributions.reduce((s, i) => s + i.amount, 0);
  const disbursementsTotal =
    includedExpenditures.reduce((s, i) => s + i.amount, 0) + refundItems.reduce((s, r) => s + r.amount, 0);
  const receiptsTotal = vaSchedules.scheduleC.reduce((s, r) => s + r.amount, 0);
  const debtsTotal = vaSchedules.scheduleF.reduce((s, r) => s + r.amount, 0);
  const loansReceived = vaSchedules.scheduleE.filter((r) => r.transactionType === 'R');
  const loansRepaid = vaSchedules.scheduleE.filter((r) => r.transactionType === 'P');

  return (
    <div className="space-y-4" data-testid="va-comet-summary">
      {/* Statement of funds — the COMET analog of the federal "Financial summary" card. */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm">
        <div className="border-b border-slate-100 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-foreground">Statement of funds</h3>
          <p className="text-xs text-muted-foreground">
            What this report rolls up to, as it will be filed on Schedule G/H.
          </p>
        </div>
        <dl className="divide-y divide-slate-100">
          <FundsLine label="Contributions & receipts received" value={gh.contributionsAndReceiptsReceived} />
          <FundsLine label="Total funds available" value={gh.totalExpendableFunds} />
          <FundsLine label="Total payments made" value={gh.totalPaymentsMade} />
          {gh.newLoanBalance !== 0 && (
            <FundsLine label="Loans outstanding at close" value={gh.newLoanBalance} muted />
          )}
          {gh.totalUnpaidDebts !== 0 && (
            <FundsLine label="Unpaid debts at close" value={gh.totalUnpaidDebts} muted />
          )}
        </dl>
      </div>

      {/* Cash-on-hand bridge — the horizontal strip, straight from the federal pattern. */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm" data-testid="va-cash-bridge">
        <div className="grid grid-cols-5 divide-x divide-slate-100 text-center">
          <BridgeCell label="Beginning cash" value={gh.beginningBalance} />
          <BridgeCell label="+ Received" value={gh.contributionsAndReceiptsReceived} tone="pos" />
          <BridgeCell label="Subtotal" value={gh.totalExpendableFunds} />
          <BridgeCell label="− Payments" value={gh.totalPaymentsMade} tone="neg" />
          <BridgeCell label="Cash at close" value={gh.endingBalance} strong />
        </div>
      </div>

      {/* Schedule A/B — contributions (editable). */}
      <ScheduleSection
        code="A"
        title="Contributions"
        blurb="Itemized direct + in-kind contributions."
        rightSummary={`${formatAmount(contributionsTotal)} · ${includedContributions.length} item${
          includedContributions.length === 1 ? '' : 's'
        }`}
        defaultOpen
        testId="va-schedule-a"
      >
        {includedContributions.length === 0 ? (
          <EmptyRow />
        ) : (
          includedContributions.map((c) => (
            <EntityRow
              key={c.id}
              testId={`va-a-${c.id}`}
              date={c.date}
              name={c.contactName}
              meta={`${c.contributionType} · ${electionDesignationLabel(c.electionDesignation, c.electionYear)}`}
              amount={c.amount}
              onEdit={onEditContribution ? () => onEditContribution(c.id) : null}
            />
          ))
        )}
      </ScheduleSection>

      {/* Schedule C — received receipts (read-only). */}
      {vaSchedules.scheduleC.length > 0 && (
        <ScheduleSection
          code="C"
          title="Rebates, refunds & interest received"
          blurb="Money received that isn't a contribution."
          rightSummary={`${formatAmount(receiptsTotal)} · ${vaSchedules.scheduleC.length} item${
            vaSchedules.scheduleC.length === 1 ? '' : 's'
          }`}
          testId="va-schedule-c"
        >
          {vaSchedules.scheduleC.map((r) => (
            <EntityRow
              key={r.contributionId}
              testId={`va-c-${r.contributionId}`}
              date={r.date}
              name={r.payerName}
              meta={r.receiptType}
              amount={r.amount}
              onEdit={null}
            />
          ))}
        </ScheduleSection>
      )}

      {/* Schedule D — expenditures (editable) + issued refunds (read-only). */}
      <ScheduleSection
        code="D"
        title="Expenditures & refunds"
        blurb="Money spent, plus contributions returned to donors."
        rightSummary={`${formatAmount(disbursementsTotal)} · ${
          includedExpenditures.length + refundItems.length
        } item${includedExpenditures.length + refundItems.length === 1 ? '' : 's'}`}
        testId="va-schedule-d"
      >
        {includedExpenditures.length === 0 && refundItems.length === 0 ? (
          <EmptyRow />
        ) : (
          <>
            {includedExpenditures.map((e) => (
              <EntityRow
                key={e.id}
                testId={`va-d-${e.id}`}
                date={e.date}
                name={e.contactName}
                meta={`${e.category}${e.purpose ? ` · ${e.purpose}` : ''}`}
                amount={e.amount}
                onEdit={onEditExpenditure ? () => onEditExpenditure(e.id) : null}
              />
            ))}
            {refundItems.map((r) => (
              <EntityRow
                key={r.id}
                testId={`va-d-refund-${r.id}`}
                date={r.date}
                name={r.contactName}
                meta="Refund of contribution"
                amount={r.amount}
                onEdit={null}
              />
            ))}
          </>
        )}
      </ScheduleSection>

      {/* Schedule E — loans, split received vs repaid (opposite flows — never summed). */}
      {vaSchedules.scheduleE.length > 0 && (
        <ScheduleSection
          code="E"
          title="Loans"
          blurb="Loans received and repaid this period. Manage loans in the ledger."
          rightSummary={loanSummary(loansReceived.length, loansRepaid.length)}
          testId="va-schedule-e"
        >
          {vaSchedules.scheduleE.map((r) => (
            <EntityRow
              key={r.id}
              testId={`va-e-${r.id}`}
              date={r.date}
              name={r.lenderName}
              meta={`${r.transactionType === 'R' ? 'loan received' : 'repayment'} · balance ${formatAmount(
                r.loanBalance,
              )}`}
              amount={r.amount}
              onEdit={null}
            />
          ))}
        </ScheduleSection>
      )}

      {/* Schedule F — unpaid debts (read-only). */}
      {vaSchedules.scheduleF.length > 0 && (
        <ScheduleSection
          code="F"
          title="Unpaid debts"
          blurb="Debts still owed at the close of this period, at their outstanding balance."
          rightSummary={`${formatAmount(debtsTotal)} · ${vaSchedules.scheduleF.length} item${
            vaSchedules.scheduleF.length === 1 ? '' : 's'
          }`}
          testId="va-schedule-f"
        >
          {vaSchedules.scheduleF.map((r) => (
            <EntityRow
              key={r.debtId}
              testId={`va-f-${r.debtId}`}
              date={r.date}
              name={r.creditorName}
              meta={`incurred · ${r.purpose}`}
              amount={r.amount}
              onEdit={null}
            />
          ))}
        </ScheduleSection>
      )}

      <OffFilingSection rows={offFilingRows} />
    </div>
  );
}

function loanSummary(received: number, repaid: number): string {
  const parts: string[] = [];
  if (received > 0) parts.push(`${received} received`);
  if (repaid > 0) parts.push(`${repaid} repaid`);
  return parts.join(' · ') || '0 items';
}

// ---------------------------------------------------------------------------
// Expandable schedule section — the header shows code + title + a right-side subtotal; the body
// (the transactions behind it) reveals on click. Mirrors the federal drill-down affordance.
// ---------------------------------------------------------------------------
function ScheduleSection({
  code,
  title,
  blurb,
  rightSummary,
  defaultOpen,
  testId,
  children,
}: {
  code: string;
  title: string;
  blurb: string;
  rightSummary: string;
  defaultOpen?: boolean;
  testId: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm" data-testid={testId}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-violet-100 text-xs font-bold text-violet-700">
          {code}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          <span className="block text-xs text-muted-foreground">{blurb}</span>
        </span>
        <span className="shrink-0 text-right text-xs font-medium tabular-nums text-muted-foreground">
          {rightSummary}
        </span>
      </button>
      {open && <div className="divide-y divide-slate-50 border-t border-slate-100">{children}</div>}
    </div>
  );
}

function EntityRow({
  date,
  name,
  meta,
  amount,
  onEdit,
  testId,
}: {
  date: string;
  name: string;
  meta: string;
  amount: number;
  onEdit: (() => void) | null;
  testId: string;
}) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 px-4 py-2 pl-14 text-sm">
      <span className="w-24 shrink-0 tabular-nums text-muted-foreground">{formatDate(date)}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-foreground">
          <span className="truncate">{name}</span>
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onEdit}
              aria-label={`Edit this transaction: ${name}`}
              title="Open in the ledger to edit this transaction"
              className="h-8 w-8 shrink-0 text-primary"
            >
              <Pencil aria-hidden="true" />
            </Button>
          )}
        </span>
        {meta && <span className="block text-xs text-muted-foreground">{meta}</span>}
      </span>
      <span className="shrink-0 tabular-nums text-foreground">{formatAmount(amount)}</span>
    </div>
  );
}

function EmptyRow() {
  return <div className="px-4 py-2 pl-14 text-sm text-muted-foreground">No rows on this filing.</div>;
}

// ---------------------------------------------------------------------------
// "In this period, not on this filing" — the excluded (Pending / Voided / status-drift) rows, one
// source, mirroring the federal off-filing section.
// ---------------------------------------------------------------------------
interface OffFilingRow {
  id: string;
  date: string;
  name: string;
  amount: number;
  inclusion: ReportRowInclusion;
}

function offFilingBadge(inclusion: ReportRowInclusion): { label: string; className: string } | null {
  switch (inclusion) {
    case 'Included':
    case 'Reported': // on the report, not off it
      return null;
    case 'ExcludedPending':
      return { label: PreflightCopy.ScheduleOffFilingNeedsReviewLabel, className: 'bg-amber-100 text-amber-800' };
    case 'ExcludedVoided':
      return { label: PreflightCopy.ScheduleExcludedVoidedLabel, className: 'bg-muted text-muted-foreground' };
    case 'ExcludedStatusDrift':
      return { label: PreflightCopy.ScheduleExcludedStatusDriftLabel, className: 'bg-muted text-muted-foreground' };
    default:
      return assertNever(inclusion);
  }
}

function OffFilingSection({ rows }: { rows: OffFilingRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div
      className="overflow-hidden rounded-lg border border-muted bg-muted/30"
      data-testid="va-off-filing-section"
    >
      <div className="px-4 py-2.5">
        <h4 className="text-sm font-semibold text-foreground">In this period, not on this filing</h4>
        <p className="text-xs text-muted-foreground">
          These are in the period but off the filing — they don&apos;t count toward the totals above.
        </p>
      </div>
      <div className="divide-y divide-slate-100 border-t border-slate-100">
        {rows.map((row) => {
          const badge = offFilingBadge(row.inclusion);
          return (
            <div
              key={row.id}
              data-testid={`va-off-filing-${row.id}`}
              className="flex items-baseline gap-2 px-4 py-1.5 text-sm text-muted-foreground"
            >
              <span className="w-20 shrink-0 tabular-nums">{formatDate(row.date)}</span>
              <span className="min-w-0 flex-1 truncate">{row.name}</span>
              <span className="w-24 shrink-0 text-right tabular-nums">{formatAmount(row.amount)}</span>
              {badge && (
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                  {badge.label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FundsLine({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between px-4 py-2 text-sm">
      <dt className={muted ? 'text-muted-foreground' : 'text-foreground/80'}>{label}</dt>
      <dd className={`tabular-nums ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>{formatAmount(value)}</dd>
    </div>
  );
}

function BridgeCell({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: number;
  strong?: boolean;
  tone?: 'pos' | 'neg';
}) {
  const valueColor = strong
    ? 'text-foreground'
    : tone === 'pos'
      ? 'text-emerald-600'
      : tone === 'neg'
        ? 'text-rose-600'
        : 'text-foreground/70';
  return (
    <div className="px-2 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 tabular-nums ${strong ? 'text-base font-bold' : 'text-sm font-medium'} ${valueColor}`}>
        {formatAmount(value)}
      </div>
    </div>
  );
}
